import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import client from '../../api/client';

// SLA Dashboard: "Team Leader - Open Tickets" and "Team Leader - Site - RE List". All data comes from the server
// (GET /reports/sla-dashboard/tl-overview/): the site-distance sheet for the list, the tickets for the open counts.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const YEARS = [2026, 2027, 2028, 2029, 2030, 2031];
const COLORS = ['#0b6aa2', '#6d4fc2', '#0f766e', '#b45309'];
const fmtDate = iso => {
  const [y, m, d] = String(iso || '').split('-');
  return y && m && d ? `${Number(d)} ${MONTHS[Number(m) - 1]} ${y}` : '';
};
const raised = t => `Raised ${fmtDate(t.created)}${t.carried ? ' - carried over from an earlier month' : ''}`;

export default function TlOverview() {
  const now = new Date();
  const [year, setYear] = useState(YEARS.includes(now.getFullYear()) ? now.getFullYear() : YEARS[0]);
  const [month, setMonth] = useState(String(now.getMonth() + 1)); // 'all' or '1'..'12'
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // 'all' or a TL key

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    client
      .get('/reports/sla-dashboard/tl-overview/', { params: { year, month } })
      .then(r => {
        if (cancelled) return;
        setData(r.data);
        setError('');
      })
      .catch(e => {
        if (cancelled) return;
        const status = e.response?.status;
        // not allowed to see it (403) or not set up on the server yet (404): simply do not show the cards
        setError(status === 403 || status === 404 ? 'hidden' : e.response?.data?.detail || 'Could not load the Team Leader details.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [year, month]);

  if (error === 'hidden' || (data && data.available === false)) return null;
  if (error && !data) return <div className="card p-5 text-sm text-red-500">{error}</div>;

  const tls = data?.tls || [];
  const rows = data?.rows || [];
  const color = key => COLORS[Math.max(0, tls.findIndex(t => t.key === key)) % COLORS.length];
  const withTickets = rows.filter(r => r.tickets && r.tickets.length);            // the list shows only what has an open ticket
  const shown = filter === 'all' ? withTickets : withTickets.filter(r => r.tl_key === filter);
  const maxTotal = Math.max(1, ...tls.map(t => t.total));
  const period = month === 'all' ? String(year) : `${MONTHS[Number(month) - 1]} ${year}`;
  const countOf = key => withTickets.filter(r => r.tl_key === key).length;
  const ticketsShown = shown.reduce((n, r) => n + (r.tickets ? r.tickets.length : 0), 0);
  const others = data?.other_open_tickets || [];
  const OTHERS_MAX = 12;

  return (
    <>
      {/* Team Leader - Open Tickets */}
      <div className="card p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="font-bold text-brand-500 text-sm">Team Leader - Open Tickets</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {data ? `Open / total tickets: ${data.total_open} / ${data.total_tickets} - ${period}` : 'Loading…'}
              {data && data.carried_open > 0 && <span> · {data.carried_open} carried over from earlier</span>}
              {data && data.unassigned_total > 0 && (
                <span title="No RE from the TL sheet is on these tickets, and their site is not in the sheet or has REs under both TLs (for example Bhopal), so they are not guessed.">
                  {' '}
                  ({data.unassigned_open} / {data.unassigned_total} not placed under a TL)
                </span>
              )}
            </p>
            <p className="text-[11px] text-gray-400 mt-0.5">
              A ticket stays here every month until it is resolved, whatever stage it is in. Total = raised in the period + carried over from earlier.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              aria-label="Year"
              value={year}
              onChange={e => setYear(Number(e.target.value))}
              className="py-2 px-3 bg-white border border-brand-100 rounded-lg text-sm font-medium"
            >
              {YEARS.map(y => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <select
              aria-label="Month"
              value={month}
              onChange={e => setMonth(e.target.value)}
              className="py-2 px-3 bg-white border border-brand-100 rounded-lg text-sm font-medium"
            >
              <option value="all">All Months</option>
              {MONTHS.map((m, i) => (
                <option key={m} value={String(i + 1)}>
                  {m}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className={loading && data ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
          {tls.map((t, i) => (
            <div key={t.key} className={i ? 'mt-5 pt-5 border-t border-brand-50' : ''}>
              <div className="grid grid-cols-[170px_1fr_130px] items-center gap-4 text-sm">
                <span className="font-semibold text-gray-800 truncate" title={t.name}>
                  {t.name}
                </span>
                <div className="relative h-6 rounded-full bg-gray-50 overflow-hidden" title={`${t.open} open of ${t.total} tickets${t.carried ? ` (${t.carried} carried over from earlier)` : ''}`}>
                  <div
                    className="absolute inset-y-0 left-0 rounded-full transition-all"
                    data-testid={`bar-total-${t.key}`}
                    style={{ width: `${(t.total * 100) / maxTotal}%`, background: `${color(t.key)}33` }}
                  />
                  <div
                    className="absolute inset-y-0 left-0 rounded-full transition-all"
                    data-testid={`bar-${t.key}`}
                    style={{ width: `${(t.open * 100) / maxTotal}%`, background: color(t.key) }}
                  />
                </div>
                <span className="text-right font-bold text-gray-800">
                  {t.open} / {t.total} tickets
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Team Leader - Site - RE List */}
      <div className="card overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 p-5">
          <div>
            <h2 className="font-bold text-brand-500 text-sm">Team Leader - Site - RE List</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {data ? `${shown.length} row${shown.length === 1 ? '' : 's'} with open tickets · ${ticketsShown} open ticket${ticketsShown === 1 ? '' : 's'}` : ''}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {[{ key: 'all', label: `All (${withTickets.length})`, c: '#3b2f63' }, ...tls.map(t => ({ key: t.key, label: `${t.name} (${countOf(t.key)})`, c: color(t.key) }))].map(
              p => (
                <button
                  key={p.key}
                  type="button"
                  aria-pressed={filter === p.key}
                  onClick={() => setFilter(p.key)}
                  className="px-4 py-2 rounded-full text-sm font-bold transition"
                  style={filter === p.key ? { background: p.c, color: '#fff' } : { background: `${p.c}1a`, color: p.c }}
                >
                  {p.label}
                </button>
              ),
            )}
          </div>
        </div>
        <div className="overflow-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-brand-50 text-xs font-bold uppercase tracking-wider text-brand-500">
                <th className="py-3 px-5">Team Leader</th>
                <th className="px-5">Site Name</th>
                <th className="px-5">RE</th>
                <th className="px-5">Open Ticket ID</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-50">
              {shown.map(r => (
                <tr key={`${r.tl_key}|${r.site}|${r.re}`} className="hover:bg-brand-50/30 transition-colors">
                  <td className="py-3 px-5">
                    <p className="font-semibold text-gray-800 text-sm">{r.tl}</p>
                    <p className="text-xs text-gray-400">Team Leader</p>
                  </td>
                  <td className="px-5 font-bold text-gray-800 text-sm">{r.site}</td>
                  <td className="px-5">
                    <span className="inline-block px-4 py-1.5 rounded-2xl bg-gray-100 text-gray-500 text-sm font-bold">{r.re}</span>
                  </td>
                  <td className="px-5 py-2">
                    {r.tickets && r.tickets.length ? (
                      <div className="flex flex-wrap gap-x-3 gap-y-1">
                        {r.tickets.map(t => (
                          <span key={t.id} className="whitespace-nowrap text-sm">
                            <Link to={`/tickets/${t.id}`} title={raised(t)} className="font-semibold text-brand-500 hover:underline">
                              {t.number}
                            </Link>
                            {t.at && <span className="text-[10px] text-gray-400"> at {t.at}</span>}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                </tr>
              ))}
              {data && shown.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center py-8 text-sm text-gray-400">
                    No open tickets with an RE from this list in this period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {filter === 'all' && others.length > 0 && (
          <div className="px-5 py-3 border-t border-brand-50 text-xs text-gray-500">
            <span className="font-semibold text-gray-600">
              {others.length} open ticket{others.length === 1 ? '' : 's'} with no RE from this list:
            </span>{' '}
            {others.slice(0, OTHERS_MAX).map((t, i) => (
              <span key={t.id}>
                {i > 0 && ', '}
                <Link
                  to={`/tickets/${t.id}`}
                  title={`${t.site || 'no site'} · ${t.re ? t.re : 'not assigned'} · ${raised(t)}`}
                  className="font-semibold text-brand-500 hover:underline"
                >
                  {t.number}
                </Link>
              </span>
            ))}
            {others.length > OTHERS_MAX && ` and ${others.length - OTHERS_MAX} more`}
          </div>
        )}
      </div>
    </>
  );
}
